import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchPollByAny,
  selectCurrentPoll,
  selectPollsLoading,
} from "@/store/redux/slices/polls_&_survey/polls_&_survey";
import { useLoader } from "@/hooks/LoaderProvider";
import Back from "@/components/specific/Back";
import HeaderImage from "@/pages/polls_&_survey/components/single_polls_&_survey/Header_Image";
import CreatorCard from "@/pages/polls_&_survey/components/single_polls_&_survey/Creator_Card";
import Button from "@/components/common/Button";
import CandidatesImage from "@/assets/images/political_stance.png";
import { ShareAltOutlined } from "@/config/antd";
import WebIcon from "@/components/common/Web_Icons";
import Success from "@/components/common/alerts/Success";

// TabIndicator component for the radio options
const TabIndicator = ({ isActive }) => (
  <div
    className={`w-5 h-5 rounded-full border border-primary flex items-center justify-center
    ${isActive ? "bg-primary" : "bg-white"}`}>
    {isActive ? (
      <div className="w-3.5 h-3.5 rounded-full bg-white flex items-center justify-center">
        <div className="w-2 h-2 rounded-full bg-primary" />
      </div>
    ) : null}
  </div>
);

TabIndicator.propTypes = {
  isActive: PropTypes.bool,
};

// Option Card component
const StanceOptionCard = ({ label, selected, onClick }) => (
  <div
    className={`p-4 rounded-lg flex justify-between items-center cursor-pointer
      ${selected ? "bg-hover" : "bg-white"} h-[85px]`}
    onClick={onClick}>
    <span className="heading-medium text-primary font-bold">{label}</span>
    <TabIndicator isActive={selected} />
  </div>
);

StanceOptionCard.propTypes = {
  label: PropTypes.string,
  selected: PropTypes.bool,
  onClick: PropTypes.func,
};

// Simplified Poll Info component without respondents section
const SimplePollInfo = ({ date, time, title, description }) => {
  return (
    <div className="text-left">
      <div className="flex justify-between">
        <div className="flex items-center justify-center gap-1">
          <h3 className="font-bold tracking-wide text-primary text-small">
            {date}
          </h3>

          <div className="bg-grey w-[.5px] h-[10px] mt-[3px]"></div>

          <h3 className="font-bold tracking-wide text-primary text-small">
            {time} <span className="ml-[.5]">PROMPT</span>
          </h3>
        </div>

        <div className="flex items-center gap-4 p-2 bg-grey">
          <span className="font-normal uppercase text-lighterGrey text-small">
            SHARE
          </span>
          <ShareAltOutlined style={{ fontSize: "15px" }} />
          <WebIcon icon="Message" size="small" />
        </div>
      </div>

      <h1 className="mb-4 heading-regular">{title}</h1>

      <p className="mb-8 font-normal leading-relaxed text-bold">
        {description}
      </p>
    </div>
  );
};

SimplePollInfo.propTypes = {
  date: PropTypes.string,
  time: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string,
};

const PoliticalStance = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const currentPoll = useSelector(selectCurrentPoll);
  const isLoading = useSelector(selectPollsLoading);
  const { showLoader, hideLoader } = useLoader();
  const [selectedOption, setSelectedOption] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // Dummy data for fields not in API response
  const dummyData = {
    creator: {
      name: "Sandra Egagharemo",
      username: "sandra_e",
      avatar: null,
      role: "Creator",
    },
  };

  useEffect(() => {
    const fetchPoll = async () => {
      if (id) {
        showLoader();
        try {
          await dispatch(fetchPollByAny({ key: "id", value: id })).unwrap();
        } catch (error) {
          console.error("Error fetching poll:", error);
        } finally {
          hideLoader();
        }
      }
    };

    fetchPoll();
  }, [dispatch, hideLoader, id, showLoader]);

  const handleSubmit = () => {
    if (!selectedOption) return;

    // Show global loader
    showLoader();

    // Simulate API call with timeout
    setTimeout(() => {
      // Hide loader and show success modal
      hideLoader();
      setShowSuccessModal(true);
    }, 2000); // 2 seconds delay to simulate API call
  };

  const handleSuccessClose = () => {
    setShowSuccessModal(false);
  };

  if (isLoading) {
    return null;
  }

  if (!currentPoll) {
    return <div>Poll not found</div>;
  }

  // Format the date from API
  const formattedDate = new Date(currentPoll.schedule.startDate)
    .toLocaleDateString("en-US", {
      day: "numeric",
      month: "long",
      year: "numeric",
    })
    .toUpperCase();

  return (
    <div className="py-6">
      <div className="mb-4">
        <Back text="Back" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 lg:gap-10">
        <div className="col-span-2">
          <HeaderImage candidates={CandidatesImage} />

          <div className="mb-6">
            <SimplePollInfo
              date={formattedDate}
              time={currentPoll.schedule.startTime}
              title={currentPoll.name}
              description={currentPoll.description}
            />
          </div>

          <div className="mb-6 rounded-lg bg-lighterBlue p-6">
            <div className="space-y-3 mb-4">
              <StanceOptionCard
                label="I am for immigration"
                selected={selectedOption === "for"}
                onClick={() => setSelectedOption("for")}
              />

              <StanceOptionCard
                label="I am not for immigration"
                selected={selectedOption === "against"}
                onClick={() => setSelectedOption("against")}
              />

              <StanceOptionCard
                label="I am on the fence"
                selected={selectedOption === "neutral"}
                onClick={() => setSelectedOption("neutral")}
              />
            </div>

            <div className="flex justify-center">
              <Button
                text="Submit"
                color="primary"
                className="w-full h-[60px] text-regular"
                customBgColor={selectedOption ? "primary" : "grey"}
                customTextColor={selectedOption ? "white" : "black"}
                onClick={handleSubmit}
                fontWeight="bold"
                disabled={!selectedOption}
              />
            </div>
          </div>
        </div>
        <div>
          <div className="p-4 mb-5 rounded-lg lg:border-2 lg:bg-lighterBlue">
            <CreatorCard isPoliticalStance={true} creator={dummyData.creator} />
          </div>
        </div>
      </div>

      {/* Success Modal */}
      <Success
        isVisible={showSuccessModal}
        onClose={handleSuccessClose}
        header="Your Political Stance"
        paragraph="Your view on Immigration will be logged to your public profile on your Political Stance's profile"
        buttonText="Close"
        onButtonClick={handleSuccessClose}
      />
    </div>
  );
};

export default PoliticalStance;
